'use client'

// Ported from the website redesign package (navy/blue/lime rebrand).
// Additive hero with an interactive shoppable-reel carousel.
// Prop-driven so it can be composed/edited from Sanity; falls back to
// the defaults below when rendered directly (e.g. on the home page).
import { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'

type Reel = {
  handle: string
  caption: string
  clock: string
  poster: string
  video: string
  likes: string
  comments: string
  tag: { top: string; left: string }
  product: { img: string; name: string; price: string }
}

export type RedesignHeroProps = {
  eyebrow?: string
  headline?: string
  lead?: string
  primaryCta?: { label: string; href: string }
  secondaryCta?: { label: string; href: string }
  meta?: { value: string; label: string }[]
  reels?: Reel[]
}

const DEFAULT_META = [
  { value: '140+', label: 'brands' },
  { value: '≈37 KB', label: 'widget' },
  { value: 'Negligible', label: 'CLS impact' },
  { value: '4 min', label: 'setup' },
]

// Product cards use the canonical Idukki marketing sample products
// (see CLAUDE.md) rather than invented names.
const DEFAULT_REELS: Reel[] = [
  {
    handle: '@creator.reel',
    caption: 'Tap any product in the video to shop it instantly.',
    clock: '9:41',
    poster:
      'https://images.unsplash.com/photo-1485125639709-a60c3a500bf1?auto=format&fit=crop&w=400&h=850&q=80',
    video: 'https://videos.pexels.com/video-files/5710143/5710143-sd_506_960_25fps.mp4',
    likes: '4.2k',
    comments: '187',
    tag: { top: '42%', left: '38%' },
    product: {
      img: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=200&h=200&q=80',
      name: 'WROGN Men Silver-Toned Watch',
      price: '$24.76 · Free shipping',
    },
  },
  {
    handle: '@studio.notes',
    caption: 'Customer stories become shoppable galleries on your store.',
    clock: '11:24',
    poster:
      'https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=400&h=850&q=80',
    video: 'https://videos.pexels.com/video-files/5710143/5710143-sd_506_960_25fps.mp4',
    likes: '8.7k',
    comments: '412',
    tag: { top: '48%', left: '45%' },
    product: {
      img: 'https://images.unsplash.com/photo-1556228578-8c89e6adf883?auto=format&fit=crop&w=200&h=200&q=80',
      name: 'Bodycon Sunscreen SPF 50',
      price: '$64.76 · Free returns',
    },
  },
  {
    handle: '@everyday.fits',
    caption: 'One lightweight widget: grid, carousel, stories and more.',
    clock: '7:08',
    poster:
      'https://images.unsplash.com/photo-1551232864-3f0890e580d9?auto=format&fit=crop&w=400&h=850&q=80',
    video: 'https://videos.pexels.com/video-files/5710143/5710143-sd_506_960_25fps.mp4',
    likes: '2.1k',
    comments: '94',
    tag: { top: '64%', left: '42%' },
    product: {
      img: 'https://images.unsplash.com/photo-1591195853828-11db59a44f6b?auto=format&fit=crop&w=200&h=200&q=80',
      name: 'Airlift High-Waist Suit Up Shorts',
      price: '$64.76 · Sizes in stock',
    },
  },
  {
    handle: '@the.drop',
    caption: 'Track clicks, add-to-carts and revenue for every post.',
    clock: '15:33',
    poster:
      'https://images.unsplash.com/photo-1485125639709-a60c3a500bf1?auto=format&fit=crop&w=400&h=850&q=80',
    video: 'https://videos.pexels.com/video-files/5710143/5710143-sd_506_960_25fps.mp4',
    likes: '5.4k',
    comments: '231',
    tag: { top: '38%', left: '48%' },
    product: {
      img: 'https://images.unsplash.com/photo-1542060748-10c28b62716f?auto=format&fit=crop&w=200&h=200&q=80',
      name: 'White Sweater Green Stripes',
      price: '$110.76 · New arrival',
    },
  },
]

const AVATARS = [
  'https://images.unsplash.com/photo-1492447166138-50c3889fccb1?auto=format&fit=crop&w=100&h=100&q=80',
  'https://images.unsplash.com/photo-1485125639709-a60c3a500bf1?auto=format&fit=crop&w=100&h=100&q=80',
  'https://images.unsplash.com/photo-1525507119028-ed4c629a60a3?auto=format&fit=crop&w=100&h=100&q=80',
]

const AUTO_MS = 6500

function HeartIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M12 21s-7-4.5-7-10a4 4 0 017-2.6A4 4 0 0119 11c0 5.5-7 10-7 10z" />
    </svg>
  )
}
function CommentIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <path
        d="M21 12a8 8 0 11-3.4-6.6L21 4l-1.4 3.4A8 8 0 0121 12z"
        fill="none"
        stroke="#fff"
        strokeWidth="2"
      />
    </svg>
  )
}

export function RedesignHero(props: RedesignHeroProps = {}) {
  const reels = props.reels?.length ? props.reels : DEFAULT_REELS
  const meta = props.meta?.length ? props.meta : DEFAULT_META
  const primaryCta = props.primaryCta ?? {
    label: 'Book a demo',
    href: 'https://app.apollo.io/#/meet/idukki/30-min-1',
  }
  const secondaryCta = props.secondaryCta ?? {
    label: 'See product tour',
    href: '/widget-tour',
  }

  const [current, setCurrent] = useState(0)
  const [exiting, setExiting] = useState<{ index: number; dir: 'next' | 'prev' } | null>(null)
  const carouselRef = useRef<HTMLDivElement>(null)
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const go = useCallback(
    (next: number, dir: 'next' | 'prev') => {
      setCurrent((cur) => {
        const target = (next + reels.length) % reels.length
        if (target === cur) return cur
        setExiting({ index: cur, dir })
        return target
      })
    },
    [reels.length],
  )

  const next = useCallback(() => go(current + 1, 'next'), [current, go])
  const prev = useCallback(() => go(current - 1, 'prev'), [current, go])

  useEffect(() => {
    if (!exiting) return
    const t = setTimeout(() => setExiting(null), 520)
    return () => clearTimeout(t)
  }, [exiting])

  // Play the active video, pause the rest.
  useEffect(() => {
    videoRefs.current.forEach((v, i) => {
      if (!v) return
      if (i === current) {
        v.muted = true
        const p = v.play()
        if (p && typeof p.catch === 'function') p.catch(() => {})
      } else {
        v.pause()
      }
    })
  }, [current])

  // Auto-advance, paused while the carousel is off-screen.
  useEffect(() => {
    const el = carouselRef.current
    if (!el) return
    const start = () => {
      if (timerRef.current) return
      timerRef.current = setInterval(() => go(current + 1, 'next'), AUTO_MS)
    }
    const stop = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => (e.isIntersecting ? start() : stop())),
      { threshold: 0.2 },
    )
    io.observe(el)
    return () => {
      io.disconnect()
      stop()
    }
  }, [current, go])

  const touch = useRef({ x: 0, y: 0 })
  const onTouchStart = (e: React.TouchEvent) => {
    touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
  }
  const onTouchEnd = (e: React.TouchEvent) => {
    const dx = e.changedTouches[0].clientX - touch.current.x
    const dy = e.changedTouches[0].clientY - touch.current.y
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) next()
      else prev()
    }
  }

  return (
    <section className="rd-hero" style={{ fontFamily: 'var(--font-body)' }}>
      <div
        className="blob"
        aria-hidden
        style={{ width: 520, height: 520, background: 'var(--blue)', opacity: 0.07, top: -160, left: -160 }}
      />
      <div
        className="blob"
        aria-hidden
        style={{ width: 380, height: 380, background: 'var(--lime)', opacity: 0.2, top: '30%', right: -100 }}
      />

      <div className="container rd-hero-grid">
        <div>
          <div className="eyebrow">
            <span className="dot" />
            {props.eyebrow ?? 'The complete UGC platform'}
          </div>
          {props.headline ? (
            <h1 style={{ marginTop: 20 }}>{props.headline}</h1>
          ) : (
            <h1 style={{ marginTop: 20 }}>
              Turn customer posts
              <br />
              into your <span className="rd-underline">best-performing</span>
              <br />
              <span className="rd-accent">shoppable shelf.</span>
            </h1>
          )}
          <p className="lead" style={{ marginTop: 24 }}>
            {props.lead ??
              'Idukki aggregates every reel, story and review your customers create, then embeds them back on your store as shoppable galleries and video. One platform. One ≈37 KB widget. Live in minutes.'}
          </p>
          <div className="rd-hero-cta">
            <a className="btn btn-primary btn-lg" href={primaryCta.href}>
              {primaryCta.label} →
            </a>
            <Link className="btn btn-ghost btn-lg" href={secondaryCta.href}>
              {secondaryCta.label}
            </Link>
          </div>
          <div className="rd-hero-meta">
            {meta.map((m) => (
              <div key={m.label}>
                <strong>{m.value}</strong> {m.label}
              </div>
            ))}
          </div>
        </div>

        <div className="rd-hero-stage">
          <div className="rd-blob-1" aria-hidden />
          <div className="rd-blob-2" aria-hidden />

          <div className="rd-float-card rd-fc-left">
            <div className="rd-avatars">
              {AVATARS.map((a) => (
                // eslint-disable-next-line @next/next/no-img-element
                <img key={a} src={a} alt="" loading="lazy" />
              ))}
            </div>
            <div className="rd-fc-label">
              New UGC tagged
              <small>in real time</small>
            </div>
          </div>

          <div className="rd-float-card rd-fc-right">
            <span
              aria-hidden
              style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--lime)' }}
            />
            <div className="rd-fc-label">
              <span style={{ color: 'var(--blue)' }}>Revenue per post</span>
              <small>tracked automatically</small>
            </div>
          </div>

          <div
            className="rd-reel"
            ref={carouselRef}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <button className="rd-reel-arrow rd-prev" aria-label="Previous reel" onClick={prev}>
              ‹
            </button>
            <button className="rd-reel-arrow rd-next" aria-label="Next reel" onClick={next}>
              ›
            </button>

            <div className="rd-reel-stage">
              {reels.map((reel, i) => {
                const cls =
                  i === current
                    ? 'rd-reel-slide rd-active'
                    : exiting?.index === i
                      ? `rd-reel-slide ${exiting.dir === 'next' ? 'rd-exit-left' : 'rd-exit-right'}`
                      : 'rd-reel-slide'
                return (
                  <div className={cls} key={i}>
                    <div className="rd-phone">
                      <div className="rd-phone-screen">
                        <video
                          ref={(el) => {
                            videoRefs.current[i] = el
                          }}
                          poster={reel.poster}
                          src={reel.video}
                          muted
                          playsInline
                          loop
                          preload={i === 0 ? 'auto' : 'metadata'}
                        />
                        <div className="rd-vid-top">
                          <span className="rd-live-dot">LIVE</span>
                          <span className="rd-clock">{reel.clock}</span>
                        </div>
                        <div className="rd-progress" />
                        <div className="rd-vid-caption">
                          <div className="rd-handle">{reel.handle}</div>
                          <div className="rd-text">{reel.caption}</div>
                        </div>
                        <div
                          className="rd-vid-tag"
                          style={{ top: reel.tag.top, left: reel.tag.left }}
                        />
                        <div className="rd-vid-rail">
                          <div className="rd-ico">
                            <HeartIcon />
                          </div>
                          <span>{reel.likes}</span>
                          <div className="rd-ico">
                            <CommentIcon />
                          </div>
                          <span>{reel.comments}</span>
                        </div>
                        <div className="rd-vid-sheet">
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img src={reel.product.img} alt="" loading="lazy" />
                          <div className="rd-info">
                            <span className="rd-name">{reel.product.name}</span>
                            <span className="rd-price">{reel.product.price}</span>
                          </div>
                          <button className="rd-add" type="button">
                            Add to cart
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="rd-reel-dots" role="tablist">
              {reels.map((_, i) => (
                <button
                  key={i}
                  className={i === current ? 'rd-dot rd-active' : 'rd-dot'}
                  aria-label={`Show reel ${i + 1}`}
                  aria-selected={i === current}
                  role="tab"
                  onClick={() => go(i, i > current ? 'next' : 'prev')}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
